/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ICittaEmarsysFormData } from './citta-emarsys-form.data';
import { ICittaEmarsysFormProps } from './citta-emarsys-form.props.autogenerated';

interface ICittaEmarsysFormFormState {
    firstName: string;
    lastName: string;
    emailAddress: string;
    buisnessName: string;
    industryPosition: string;
    privacyPolicyChecked: boolean;
    eAValError: boolean;
    eAError: boolean;
    fNError: boolean;
    lNError: boolean;
    privacyError: boolean;
    rCCError: boolean;
    reqError: boolean;
    responseReceived: boolean;
    responseError: boolean;
    responseSuccess: boolean;
    showResponseSuccess: boolean;
    reCaptchaChecked: boolean;
    charactersLeft?: number;
    firstNameErrorClass: string;
    lastNameErrorClass: string;
    emailErrorClass: string;
    privacyErrorClass: string;
}

export interface ICittaEmarsysFormForm {
    FormWrapper: INodeProps;
    FormContainer: INodeProps;
    firstAndLastNameInput: React.ReactNode;
    emailInput: React.ReactNode;
    buisnessInput: React.ReactNode;
    industryInput: React.ReactNode;
    checkboxPrivacyPolicy: React.ReactNode;
    reCaptcha: React.ReactNode;
    subscribeButtton: React.ReactNode;
    subscribeThanks: React.ReactNode;
}


export interface ICittaEmarsysFormViewProps extends ICittaEmarsysFormProps<ICittaEmarsysFormData> {
    SubscribeWrapper: IModuleProps;
    subscribeResponse: React.ReactNode;
    subscribeForm: ICittaEmarsysFormForm;
    responseSuccess: boolean;
    showResponseSuccess: boolean;
    formErrors: React.ReactNode;
    formErrorsNodeProps: INodeProps;
    successResponse: React.ReactNode;
    showSuccessResponse: boolean;
    successResponseNodeProps: INodeProps;
}

/**
 *
 * CittaEmarsysForm component
 * @extends {React.PureComponent<ICittaEmarsysFormProps<ICittaEmarsysFormData>>}
 */
class CittaEmarsysForm extends React.PureComponent<ICittaEmarsysFormProps<ICittaEmarsysFormData>, ICittaEmarsysFormFormState> {
    private _FirstNameInputRef: React.RefObject<HTMLInputElement>;
    private _LastNameInputRef: React.RefObject<HTMLInputElement>;
    private _EmailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _IndustryPositionInputRef: React.RefObject<HTMLInputElement>
    private _BuisnessNameInputRef: React.RefObject<HTMLInputElement>
    private _PrivacyPolicyRef: React.RefObject<HTMLInputElement>;
    private _recaptchaRef: React.RefObject<ReCAPTCHA>;

    private firstNameValue: boolean = false;
    private lastNameValue: boolean = false;
    private emailAddressValue: boolean = false;
    private formInputValues: boolean = false;
    private responseMessage: string | undefined;
    private moduleClassName: string = 'newsletterSubWrap';
    private recaptchaErrorMessage: string | undefined;

    constructor(props: ICittaEmarsysFormProps<ICittaEmarsysFormData>) {
        super(props);
        this._FirstNameInputRef = React.createRef();
        this._LastNameInputRef = React.createRef();
        this._EmailAddressInputRef = React.createRef();
        this._BuisnessNameInputRef = React.createRef();
        this._IndustryPositionInputRef = React.createRef();
        this._PrivacyPolicyRef = React.createRef();
        this._recaptchaRef = React.createRef();
        this._handleSubmit = this._handleSubmit.bind(this);

        this.state = {
            firstName: '',
            lastName: '',
            emailAddress: '',
            buisnessName: '',
            industryPosition: '',
            privacyPolicyChecked: false,
            eAValError: false,
            fNError: false,
            lNError: false,
            eAError: false,
            privacyError: false,
            rCCError: false,
            reqError: false,
            responseReceived: false,
            responseError: false,
            reCaptchaChecked: false,
            responseSuccess: false,
            showResponseSuccess: false,
            charactersLeft: Number(this.props.resources.messageCharactersLimit) || 140,
            firstNameErrorClass: 'hide',
            lastNameErrorClass: 'hide',
            emailErrorClass: 'hide',
            privacyErrorClass: 'hide'
        };
    }

    public render(): JSX.Element | null {
        const viewProps: ICittaEmarsysFormViewProps = {
            ...(this.props as ICittaEmarsysFormProps<ICittaEmarsysFormData>),
            SubscribeWrapper: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, this.props.config.className)
            },
            subscribeResponse: this._getSubmitResponse(),
            subscribeForm: this._renderForm(),
            responseSuccess: this.state.responseSuccess,
            showResponseSuccess: this.state.showResponseSuccess,
            formErrors: this._renderErrors(),
            formErrorsNodeProps: {
                className: 'emarsys-form-errors'
            },
            successResponse: this._renderSuccessResponse(),
            showSuccessResponse: this.state.responseSuccess,
            successResponseNodeProps: {
                className: 'emarsys-form-success'
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getSubmitResponse(): React.ReactNode {
        return (
            this.state.responseReceived && this.responseMessage !== '' || undefined
                ? (
                    <div className={`response-message ${this.state.responseError ? 'response-error' : ''}`}>
                        <p className='response-message-content'>{this.responseMessage}</p>
                    </div>
                )
                : null
        );
    }
    
    private _renderSuccessResponse(): React.ReactNode {
        return (
            this.state.responseSuccess
                ? (
                    <div className="response-succcess">
                        <h2>{this.props.config.successTextResponse}</h2>
                    </div>
                )
                : null
        );
    }
    
    
    private onRecaptchaChange = (value: string | null) => {
        if (value) {
            this.setState({ reCaptchaChecked: true, rCCError: false });
        } else {
            this.setState({ reCaptchaChecked: false });
        }
    }

    private _renderForm(): ICittaEmarsysFormForm {
        return {
            FormWrapper: {
                className: 'emarsys-form',
            },
            FormContainer: {
                className: 'emarsys-form-div',
            },
            firstAndLastNameInput: this._renderFirstAndLastNameInput(),
            emailInput: this._renderEmailInput(),
            buisnessInput: this._renderBuisnessNameInput(),
            industryInput: this._renderIndustryPositionInput(),
            checkboxPrivacyPolicy: this._renderCheckboxPrivacyPolicy(),
            reCaptcha: this._renderReCaptcha(),
            subscribeButtton: this._renderSubmitButton(),
            subscribeThanks: this._renderSubmitThanks()
        };
    }

    private _renderFirstAndLastNameInput(): React.ReactNode {
        return (
            <div className='name-input-container'>
                <div className='name-input'>
                    <input
                        ref={this._FirstNameInputRef}
                        type='text'
                        onChange={this._handleChange('firstName')}
                        aria-label='First Name'
                        className='first-name-input-text'
                        placeholder={this.props.config.firstNamePlaceholderText}
                        value={this.state.firstName}
                        required
                    />
                </div>
                <div className='name-input'>
                    <input
                        ref={this._LastNameInputRef}
                        type='text'
                        onChange={this._handleChange('lastName')}
                        aria-label='First Name'
                        className='name-input-text'
                        placeholder={this.props.config.lastNamePlaceholderText}
                        value={this.state.lastName}
                        required
                    />
                </div>
            </div>
        );
    }

    private _renderEmailInput(): React.ReactNode {
        return (
            <div className='email-address-input'>
                {this.state.eAError || this.state.eAValError ? this._renderAlert('email-address-input') : ''}
                <input
                    ref={this._EmailAddressInputRef}
                    type='email'
                    onChange={this._handleChange('emailAddress')}
                    aria-label='Email Address'
                    className='email-address-input-text'
                    value={this.state.emailAddress}
                    required
                    placeholder={this.props.config.emailInputPlaceholder}
                />
            </div>
        );
    }

    private _renderBuisnessNameInput(): React.ReactNode {
        return (
            <div className='buisness-name-input'>
                {this.state.eAError || this.state.eAValError ? this._renderAlert('buisness-name-input') : ''}
                <input
                    ref={this._BuisnessNameInputRef}
                    type='text'
                    onChange={this._handleChange('buisnessName')}
                    aria-label='Buisness Name'
                    className='buisness-name-input-text'
                    value={this.state.buisnessName}
                    required
                    placeholder={this.props.config.businessNamePlaceholder}
                />
            </div>
        );
    }
    private _renderIndustryPositionInput(): React.ReactNode {
        return (
            <div className='industry-position-input'>
                {this.state.eAError || this.state.eAValError ? this._renderAlert('industry-position-input') : ''}
                <input
                    ref={this._IndustryPositionInputRef}
                    type='text'
                    onChange={this._handleChange('industryPosition')}
                    aria-label='Industry Position'
                    className='industry-position-input-text'
                    value={this.state.industryPosition}
                    required
                    placeholder={this.props.config.industryPositionPlaceholder}
                />
            </div>
        );
    }

    private _renderAlert(className: string): JSX.Element {
        return <></>;
    }

    private _renderCheckboxPrivacyPolicy(): React.ReactNode {
        return (
            <div className='checkbox-privacy-policy'>
                {this.state.privacyError ? this._renderAlert('checkbox-privacy-policy') : ''}
                <div className='checkbox-privacy-policy-content'>
                    <input
                        ref={this._PrivacyPolicyRef}
                        type='checkbox'
                        className='privacy-policy-text'
                        aria-label='Accept Privacy Policy'
                        aria-checked='false'
                        onChange={this._handleChange('privacyPolicy')}
                        id='privacyPolicyCheckbox'
                    />
                    <span className='checkbox-privacy-policy-text'>
                        <p>{this.props.config.privacyPolicyText}<a href={this.props.config.privacyPolicyLink}>Privacy Policy</a></p>
                    </span>
                </div>
            </div>
        );
    }


    private _renderReCaptcha(): React.ReactNode {
        return (
            <div>
                {
                    this.props.config.reCaptchaOption ? <div className='recaptcha newsletter-recapture-visable'>
                        {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
                        <ReCAPTCHA
                            ref={this._recaptchaRef}
                            sitekey={this.props.config.reCaptchaSiteKey!}
                            onChange={this.onRecaptchaChange}
                        />
                    </div> : null
                }
            </div>
        );
    }
    
    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private _renderSubmitButton(): React.ReactNode {
        return (
            <div className="subscribe-button-container">
                <Button
                    className={`subscribe-button btn-thirdary`}
                    title={this.props.config.submitButtonLabel}
                    color='primary'
                    onClick={this._handleSubmit}
                >{this.props.config.submitButtonLabel}
                </Button>
            </div>
        );
    }

    private _renderSubmitThanks(): React.ReactNode {
        return (
            <div>
                <div className={`subscribe-thanks`}>
                    Thanks!
                </div>
            </div>
        );
    }

    private _showErrors = (): void => {
        this.setState({
            emailErrorClass: this._checkEmailError() ? 'show' : 'hide',
            firstNameErrorClass: (this._FirstNameInputRef.current && this._FirstNameInputRef.current.value.length === 0) ? 'show' : 'hide',
            lastNameErrorClass: (this._LastNameInputRef.current && this._LastNameInputRef.current.value.length === 0) ? 'show' : 'hide',
            privacyErrorClass: (this._PrivacyPolicyRef.current && !this._PrivacyPolicyRef.current.checked) ? 'show' : 'hide'
        });
    }

    private _checkEmailError = (): boolean => {
        const email: string = this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value || '';
        if (email.length === 0) {
            return true;
        } else {
            return !this._isEmailValid(email);
        }
    }

    private _renderErrors = (): JSX.Element => {
        const {resources} = this.props;
        return (
            <>
                <div className={this.state.firstNameErrorClass}>
                    {resources.firstNameError}
                </div>
                <div className={this.state.lastNameErrorClass}>
                    {resources.lastNameError}
                </div>
                <div className={this.state.emailErrorClass}>
                    {resources.emailError}
                </div>
                <div className={this.state.privacyErrorClass}>
                    {resources.privacyError}
                </div>
                
            </>
        );
    }

    private _handleChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        switch (name) {
            case 'firstName': {
                this.setState({ firstName: value, fNError: false, reqError: false, eAValError: false, privacyError: false });
                break;
            }
            case 'lastName': {
                this.setState({ lastName: value, lNError: false, reqError: false, eAValError: false, privacyError: false });
                break;
            }
            case 'emailAddress': {
                this.setState({ emailAddress: value, eAError: false, reqError: false, eAValError: false, privacyError: false });
                break;
            }
            case 'buisnessName': {
                this.setState({ buisnessName: value, eAError: false, reqError: false, eAValError: false, privacyError: false });
                break;
            }
            case 'industryPosition': {
                this.setState({ industryPosition: value, reqError: false, eAValError: false, privacyError: false });
                break;
            }
            case 'privacyPolicy': {
                const isChecked = this._PrivacyPolicyRef.current && this._PrivacyPolicyRef.current.checked ? this._PrivacyPolicyRef.current.checked : false;
                const recaptchaWrapper = document.querySelector('.recaptcha');
                recaptchaWrapper && recaptchaWrapper.classList.toggle('newsletter-recapture-visable', !isChecked);
                this.setState({ privacyPolicyChecked: isChecked, reqError: false, eAValError: false, privacyError: false });
                break;
            }
            default: {
                console.log('No input box changes');
                this.setState({ reqError: false, eAValError: false, privacyError: false });
            }
        }
    }

    private _isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private async _handleSubmit(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): Promise<void> {
        this._showErrors();
        
        this.firstNameValue = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? true : false;
        this.lastNameValue = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? true : false;
        this.emailAddressValue = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? true : false;        

        const fN = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? this._FirstNameInputRef.current.value : '';
        const lN = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? this._LastNameInputRef.current.value : '';
        const eA = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? this._EmailAddressInputRef.current.value : '';

        const bN = this.state.buisnessName;
        const iP = this.state.industryPosition;

        const eAVal = this._EmailAddressInputRef.current!.value;
        const oI = this._PrivacyPolicyRef.current!.checked;

        if (!this.firstNameValue) {
            this.setState({
                fNError: true,
                reqError: true
            });
        } else if (!this.lastNameValue) {
            this.setState({
                lNError: true,
                reqError: true
            });
         } else if (!this.emailAddressValue) {
            this.setState({
                eAError: true,
                reqError: true
            });
        } else if (!this._isEmailValid(eAVal)) {
            this.setState({
                eAValError: true
            });
        } else if (this.props.config.reCaptchaOption && !this.state.reCaptchaChecked) {
            this.setState({
                rCCError: true
            });
        } else if (!this._PrivacyPolicyRef.current!.checked) {
            this.setState({
                privacyPolicyChecked: false,
                privacyError: true
            });
        } else {
            this.formInputValues = (this.emailAddressValue) ? true : false;
            if (this.formInputValues && this.state.privacyPolicyChecked && !this.state.eAValError) {
                try {
                    await this._postEntry(fN, lN, eA, bN, oI, iP);

                    this._clearValues();
                    this._recaptchaRef.current?.reset();
                    
                } catch (e) {
                    console.log(e, 'Please fill in the form!');
                }
            }
        }
    }

    private async _postEntry(firstName: string, lastName: string, email: string, businessName: string, optIn: boolean, industryPosition: string): Promise<void> {
        let emarsysFieldId = this.props.config.emarsysGroupId, 
        industryPositionFieldId = this.props.config.emarsysEmarsysGroupId;

        const response = await MsDyn365.commerceApiRequest(
            this.props.context.request,
            'PostEmarsysContactEvent',
            'post',
            {
                firstName,
                lastName,
                businessName,
                emailAddress: email,
                industryPosition,
                emarsysFieldId,
                industryPositionFieldId,
            }
        ) as any;
        
        this.responseMessage = response?.data.Message;

        if (response?.data.Result !== 'Error') {
            this.setState({
                responseReceived: true,
                responseError: response?.data.Result === 'Error',
                responseSuccess: response?.data.Result !== 'Error',
                showResponseSuccess: true,
                emailAddress: '',
                firstName: '',
                lastName: '',
                industryPosition: '',
                buisnessName: '',
                privacyPolicyChecked: false,
                reqError: false,
                eAValError: false,
                rCCError: false
            });
        } else {
            this.setState({
                responseReceived: true,
                responseError: response?.data.Result === 'Error',
                responseSuccess: response?.data.Result !== 'Error',
                emailAddress: '',
                privacyPolicyChecked: false,
                reqError: false,
                eAValError: false,
                rCCError: false
            });
        }
    }

    private _clearValues(): void {
        if (this._PrivacyPolicyRef.current) {
            this._PrivacyPolicyRef.current.checked = false;
            const recaptchaWrapper = document.querySelector('.recaptcha');
            recaptchaWrapper && recaptchaWrapper.classList.toggle('newsletter-recapture-visable', true);
        }
        if (this._EmailAddressInputRef.current) {
            this._EmailAddressInputRef.current!.value = '';
        }
        if (this._FirstNameInputRef.current) {
            this._FirstNameInputRef.current!.value = '';
        }
        if (this._LastNameInputRef.current) {
            this._LastNameInputRef.current!.value = '';
        }
        if (this._BuisnessNameInputRef) {
            this._BuisnessNameInputRef.current!.value = '';
        }
        if (this._IndustryPositionInputRef) {
            this._IndustryPositionInputRef.current!.value = '';
        }
    }

}

export default CittaEmarsysForm;


